import { useState, useRef, useEffect } from "react";
import { BiBold, BiCode, BiImage, BiItalic, BiLink, BiUnderline } from "react-icons/bi";
import { BsQuote } from "react-icons/bs";
import { LuHeading1, LuHeading2 } from "react-icons/lu";
import { FaList, FaListOl } from "react-icons/fa";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";

const MAX_EMBED_BYTES = 350 * 1024;
const MAX_IMAGE_DIMENSION = 1600;
const IMAGE_QUALITY = 0.82;

const compressImageFile = (file) => {
  return new Promise((resolve, reject) => {
    if (file.type === "image/gif") {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const img = new window.Image();
      img.onload = () => {
        const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", IMAGE_QUALITY));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export const RichTextEditor = ({ value, onChange }) => {
  const fileInputRef = useRef(null);
  const editorRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [notice, setNotice] = useState(null);

  const insertImageFile = async (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    setNotice(null);
    try {
      const dataUrl = await compressImageFile(file);
      const approxBytes = dataUrl.length; // base64 chars ≈ bytes actually sent over the wire
      if (approxBytes > MAX_EMBED_BYTES) {
        setNotice(
          `"${file.name}" is still ~${(approxBytes / 1024).toFixed(0)}KB after compression — try a smaller image.`
        );
        return;
      }
      editorRef.current
        ?.chain()
        .focus()
        .setImage({ src: dataUrl, alt: file.name.replace(/\.[^/.]+$/, "") })
        .run();
    } catch {
      setNotice(`Couldn't process "${file.name}" — try a different image.`);
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false, autolink: true }),
      Image.configure({
        allowBase64: true,
        HTMLAttributes: { style: "max-width:100%;border-radius:10px;margin:10px 0;display:block;" },
      }),
    ],
    content: value || "<p></p>",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none min-h-[200px] px-4 py-3 text-sm leading-relaxed text-slate-800 focus:outline-none [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-1 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_blockquote]:border-l-2 [&_blockquote]:border-slate-300 [&_blockquote]:pl-3 [&_blockquote]:text-slate-500 [&_blockquote]:italic [&_pre]:bg-slate-900 [&_pre]:text-slate-100 [&_pre]:rounded-md [&_pre]:p-3 [&_pre]:text-xs [&_pre]:overflow-x-auto [&_img]:max-w-full [&_img]:rounded-lg [&_a]:text-cyan-700 [&_a]:underline",
      },
      handlePaste: (_view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;
        for (const item of items) {
          if (item.type.startsWith("image/")) {
            event.preventDefault();
            insertImageFile(item.getAsFile());
            return true;
          }
        }
        return false;
      },
      handleDrop: (_view, event) => {
        const file = event.dataTransfer?.files?.[0];
        if (file && file.type.startsWith("image/")) {
          event.preventDefault();
          insertImageFile(file);
          return true;
        }
        return false;
      },
    },
  });

  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);
  useEffect(() => {
    if (!editor || editor.isFocused) return
    const current = editor.getHTML();
    if (value !== current) {
      editor.commands.setContent(value || "<p></p>", false);
    }
  }, [value, editor]);
  const handleImageButtonClick = (e) => {
    e.preventDefault();
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e) => {
    insertImageFile(e.target.files?.[0]);
    e.target.value = "";
  };

  const handleLink = (e) => {
    e.preventDefault();
    if (!editor) return;
    if (editor.isActive("link")) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    const url = window.prompt("Link URL");
    if (url) editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  if (!editor) return null;

  const tools = [
    { icon: <LuHeading1 size={15} />, label: "Heading 1", action: () => editor.chain().focus().toggleHeading({ level: 2 }).run(), active: editor.isActive("heading", { level: 2 }) },
    { icon: <LuHeading2 size={15} />, label: "Heading 2", action: () => editor.chain().focus().toggleHeading({ level: 3 }).run(), active: editor.isActive("heading", { level: 3 }) },
    { icon: <BiBold size={15} />, label: "Bold", action: () => editor.chain().focus().toggleBold().run(), active: editor.isActive("bold") },
    { icon: <BiItalic size={15} />, label: "Italic", action: () => editor.chain().focus().toggleItalic().run(), active: editor.isActive("italic") },
    { icon: <BiUnderline size={15} />, label: "Underline", action: () => editor.chain().focus().toggleUnderline().run(), active: editor.isActive("underline") },
    { icon: <FaList size={15} />, label: "Bulleted list", action: () => editor.chain().focus().toggleBulletList().run(), active: editor.isActive("bulletList") },
    { icon: <FaListOl size={15} />, label: "Numbered list", action: () => editor.chain().focus().toggleOrderedList().run(), active: editor.isActive("orderedList") },
    { icon: <BsQuote size={15} />, label: "Quote", action: () => editor.chain().focus().toggleBlockquote().run(), active: editor.isActive("blockquote") },
    { icon: <BiCode size={15} />, label: "Code block", action: () => editor.chain().focus().toggleCodeBlock().run(), active: editor.isActive("codeBlock") },
    { icon: <BiLink size={15} />, label: "Link", action: handleLink, active: editor.isActive("link") },
    { icon: <BiImage size={15} />, label: "Insert image", action: handleImageButtonClick, standout: true },
  ];

  return (
    <div
      className={`overflow-hidden rounded-lg border bg-white transition-colors ${isDragOver ? "border-cyan-600 ring-2 ring-cyan-600/40" : "border-slate-300 focus-within:ring-2 focus-within:ring-cyan-600 focus-within:border-cyan-600"
        }`}
    >
      <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-200 bg-slate-50 px-2 py-1.5">
        {tools.map((t, i) => (
          <button
            key={i}
            type="button"
            title={t.label}
            onMouseDown={(e) => e.preventDefault()}
            onClick={t.action}
            className={`rounded-md p-1.5 transition-colors ${t.active
                ? "bg-cyan-100 text-cyan-700"
                : t.standout
                  ? "text-cyan-700 hover:bg-cyan-50"
                  : "text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm"
              }`}
          >
            {t.icon}
          </button>
        ))}
      </div>

      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileInputChange} />

      <div className="relative">
        <EditorContent
          editor={editor}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={() => setIsDragOver(false)}
        />
        {isDragOver && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-cyan-50/80">
            <p className="flex items-center gap-2 rounded-lg bg-white px-3 py-1.5 text-xs font-medium text-cyan-700 shadow-sm">
              <BiImage size={14} /> Drop image to insert
            </p>
          </div>
        )}
      </div>

      <div className="flex items-center gap-1.5 border-t border-slate-100 bg-slate-50/60 px-3 py-1.5 text-[11px] text-slate-400">
        <BiImage size={12} />
        {notice ? <span className="text-rose-500">{notice}</span> : "Drag & drop, paste, or use the image button to add images"}
      </div>
    </div>
  );
}